/* eslint-disable react-hooks/exhaustive-deps,import/no-unresolved */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useRouteMatch, useHistory } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';

import { CardOrderComponent, OrderCardMobileComponent } from 'assets/components';
import { CONNECT_FINANCIAL_CARD_ORDER_STATUS, ORDERABLE_CARD_TYPES } from '../queries';
import {
  mainCardTypes,
  CardState,
  CONNECT_FINANCIAL_ORDER_STATUS,
  isConnectFinancial,
  orderCardFlowScreens,
  resolveConnectFinancialOrderPollInterval,
  resolveOrderLandingCardStatus,
} from '../constants';
import { VerificationState, TIER_LEVEL } from '../../../utilities/constants';
import CardLayout from '../../shared/CardLayout';
import { GenericLoading } from '../../../components/Placeholders';
import { showAlert } from '../../../utilities/notifications';
import {
  useCardStatus,
  useUserCardProvider,
  useUserPersonalInfo,
} from '../../../utilities/sharedHooks/user';
import {
  USER_DETAILS_QUERY_KEY,
  SETTINGS_SUPPORTED_COUNTRIES_QUERY_KEY,
  USER_REGION_SETTINGS_QUERY_KEY,
  USER_CARD_INFO,
} from '../../../utilities/sharedQueries';

import { useSetting } from '../../../utilities/sharedHooks/settings';
import useRiskTierSettings from '../../../utilities/sharedHooks/useRiskTierSettings';
import ModalLayout from '../../shared/ModalLayout';
import InfoPopup from '../../shared/Modals/InfoPopup';
import { useLedgerAccounts } from '../../../utilities/sharedHooks/ledgerSDKHooks';
import { STORE_LIVE_ADDRESSES } from '../../Onboarding/mutations';

const NO_ORDER_STATUS_ERRORS = [403, 404];

const OrderCard = () => {
  const { path } = useRouteMatch();
  const { state } = useLocation();
  const { t: translate } = useTranslation();
  const [isCardStatusChecked, setCardStatusChecked] = useState(false);
  const {
    cardProvider: userCardProvider,
    isLoading: isLoadingCardProvider,
    refetch: refetchUserCardProvider,
  } = useUserCardProvider(false);
  const [bottomValue, setBottomValue] = useState(0);
  const history = useHistory();
  const { data: userData } = useQuery(USER_DETAILS_QUERY_KEY);
  const { data: supportedCountries, isLoading: isLoadingSupportedCountries } = useQuery(
    SETTINGS_SUPPORTED_COUNTRIES_QUERY_KEY
  );
  const [settings, isLoadingSettings] = useSetting('server_configurations', [
    'card_support_email_address',
    'support_email_address',
  ]);

  const { verificationState, isUS: isUsUser } = useUserPersonalInfo();
  const isUserNotVerified = verificationState && verificationState === VerificationState.VERIFIED;

  const { refetch: refetchUserCardInfo } = useQuery(USER_CARD_INFO, {
    enabled: false,
    onSettled: (data, error) => {
      if (!error) {
        if (data && data?.hasCard) {
          history.push(`${path}details`);
        }
      }
    },
  });

  const {
    card_support_email_address: cardSupportEmailAddress,
    support_email_address: ledgerSupportEmailAddress,
  } = settings || {
    card_support_email_address: '',
    support_email_address: '',
  };

  const { riskAssessment } = useRiskTierSettings();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentScreen, setCurrentScreen] = useState('');
  const { data: cardOptionsData } = useQuery(ORDERABLE_CARD_TYPES);

  const showScreen = (screen) => {
    setCurrentScreen(screen);
    setIsModalVisible(true);
  };

  const cardTermsInfoOptions = {
    title: translate('terms_of_card_label'),
    content: () => (
      <div className="mt-4">
        <p className="text-white text-f5">{translate('us_bank_disclaimer_visa_2')}</p>
      </div>
    ),
    isButtonInFooter: false,
    isScrollable: false,
  };

  const [ledgerAccounts] = useLedgerAccounts(true);

  const { mutate: storeLiveAddress } = useMutation(STORE_LIVE_ADDRESSES);

  const storeUserLiveAddresses = useCallback(() => {
    if (ledgerAccounts && Array.isArray(ledgerAccounts) && ledgerAccounts?.length > 0) {
      const liveAddresses = ledgerAccounts.map((item) => ({
        coin: item?.coin,
        address: item?.account?.address,
      }));
      storeLiveAddress({ live_addresses: liveAddresses });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ledgerAccounts]);

  useEffect(() => {
    refetchUserCardProvider();
    refetchUserCardInfo();
  }, []);

  useEffect(() => {
    if (ledgerAccounts && Array.isArray(ledgerAccounts) && ledgerAccounts?.length > 0) {
      storeUserLiveAddresses();
    }
  }, [ledgerAccounts]);

  const isCardOrderSupported = useMemo(() => {
    if (userData && userData.user && supportedCountries) {
      const userCountry = supportedCountries.find(
        (country) => country.id === userData?.user?.country_id
      );
      return (
        userCountry?.can_order_cards && !userCountry.is_sanctioned && userCountry.risk_level < 2
      );
    }
    return false;
  }, [userData, supportedCountries]);

  useEffect(() => {
    // eslint-disable-next-line no-restricted-globals
    switch (screen.height) {
      case 640:
        setBottomValue(60);
        break;
      case 667:
        setBottomValue(74);
        break;
      case 1218:
        setBottomValue(74);
        break;
      default:
        setBottomValue(95);
        break;
    }
    // eslint-disable-next-line no-restricted-globals
  }, [screen.height]);

  const toggleModal = () => {
    if (!isCardOrderSupported) {
      showAlert({
        message: translate('feature_not_supported_in_area').replace(
          '%s',
          `<a class="link" href="mailto:${cardSupportEmailAddress}">${cardSupportEmailAddress}</a>`
        ),
        type: 'danger',
      });
    } else {
      history.push('/dashboard/card/order-card');
    }
  };

  const orderableCardTypes = useMemo(() => {
    if (cardOptionsData?.orderableCardTypes) {
      return [
        ...new Set(
          cardOptionsData.orderableCardTypes
            .flatMap((provider) => provider.cards || [])
            .map((card) => card.type)
            .filter(Boolean)
        ),
      ];
    }
    return [];
  }, [cardOptionsData]);

  const {
    data: cardStatus,
    isLoading: isLoadingCardStatus,
    refetch: getCardStatus,
  } = useCardStatus({
    enabled: false,
    onSettled: (_, error) => {
      if (error) {
        // If user doesn't have a card, it's not an error
        if (error.status !== 404 && error.status !== 403) {
          history.push(`${path}error`, { cardHasIssue: true });
        }
      }
    },
  });

  useEffect(() => {
    if (state?.totpVerified) {
      showAlert({
        message: translate('totp_verify_success_msg'),
        type: 'success',
        blockAutoDismiss: true,
        showIcon: true,
      });
    }
  }, []);

  const { data: userRegionSettings } = useQuery(USER_REGION_SETTINGS_QUERY_KEY);

  const {
    data: connectFinancialOrderStatus,
    isLoading: isLoadingConnectFinancialOrderStatus,
    error: connectFinancialOrderStatusError,
  } = useQuery(CONNECT_FINANCIAL_CARD_ORDER_STATUS, {
    enabled: isConnectFinancial(userCardProvider),
    refetchInterval: resolveConnectFinancialOrderPollInterval,
  });

  const orderStatusError = NO_ORDER_STATUS_ERRORS.includes(connectFinancialOrderStatusError?.status)
    ? null
    : connectFinancialOrderStatusError;

  useEffect(() => {
    if (connectFinancialOrderStatus?.status === CONNECT_FINANCIAL_ORDER_STATUS.CONFIRMED) {
      refetchUserCardInfo();
    }
  }, [connectFinancialOrderStatus?.status]);

  useEffect(() => {
    if (orderStatusError && orderStatusError.status !== 401) {
      history.push(`${path}error`, { cardHasIssue: true });
    }
  }, [orderStatusError]);

  const landingCardStatus = resolveOrderLandingCardStatus(
    userCardProvider,
    cardStatus,
    connectFinancialOrderStatus
  );

  const cardType = useMemo(() => {
    if (orderableCardTypes?.includes(mainCardTypes.VIRTUAL.toUpperCase())) {
      return mainCardTypes.VIRTUAL;
    }
    if (orderableCardTypes?.includes(mainCardTypes.PHYSICAL.toUpperCase())) {
      return mainCardTypes.PHYSICAL;
    }
    return userRegionSettings?.default_card_type?.toLowerCase();
  }, [userRegionSettings, orderableCardTypes]);

  const defaultNetworkForCardTypeObj = useMemo(() => {
    let result = null;
    if (
      cardOptionsData &&
      userRegionSettings &&
      cardOptionsData.orderableCardTypes &&
      cardOptionsData.orderableCardTypes.length > 0
    ) {
      result = {};
      cardOptionsData?.orderableCardTypes.forEach((item) => {
        const { cards } = item;
        if (cards && cards.length === 1) {
          result[item.provider.toLowerCase()] = cards[0].processor;
        } else if (cards && cards.length > 1 && userRegionSettings?.default_card_type) {
          const matchingCard = cards.find(
            (card) => card.type === userRegionSettings.default_card_type.toUpperCase()
          );
          result[item.provider.toLowerCase()] = matchingCard?.processor;
        }
      });
    }
    return result;
  }, [cardOptionsData, userRegionSettings]);

  const defaultNetwork = defaultNetworkForCardTypeObj
    ? defaultNetworkForCardTypeObj[userCardProvider]
    : 'MASTERCARD';

  const isOrderCardAllowed = useMemo(
    () =>
      userRegionSettings?.can_order_card &&
      verificationState !== VerificationState.PENDING &&
      verificationState !== VerificationState.CHECKED &&
      (riskAssessment?.userRisk?.key !== TIER_LEVEL.FOUR ||
        (riskAssessment?.userRisk?.key === TIER_LEVEL.FOUR &&
          riskAssessment?.userRisk?.order_card) ||
        (riskAssessment?.userRisk?.key === TIER_LEVEL.FOUR &&
          !riskAssessment?.userRisk?.order_card &&
          !riskAssessment?.userNextTier?.hasActiveChangeTierRequest?.id)),

    [userRegionSettings, verificationState, riskAssessment]
  );

  const isBannerVisible = useMemo(() => {
    if (
      verificationState === VerificationState.PENDING ||
      verificationState === VerificationState.CHECKED ||
      (landingCardStatus && landingCardStatus?.cardState === CardState.PENDING)
    ) {
      return true;
    }
    return false;
  }, [verificationState, landingCardStatus]);

  useEffect(() => {
    if (userCardProvider) {
      getCardStatus();
    }

    setCardStatusChecked(true);
  }, [userCardProvider]);

  if (
    !isCardStatusChecked ||
    isLoadingCardStatus ||
    isLoadingConnectFinancialOrderStatus ||
    orderStatusError ||
    isLoadingCardProvider ||
    isLoadingSettings ||
    isLoadingSupportedCountries
  ) {
    return <GenericLoading isFullScreen={false} />;
  }

  const orderCardHandler = () => {
    if (riskAssessment.settings) {
      const { key: userTier, order_card: allowOrderCard } = riskAssessment.userRisk;
      switch (userTier) {
        case TIER_LEVEL.ZERO:
          history.push(`/kyc`, { switch: true });
          break;
        case TIER_LEVEL.FOUR:
          if (allowOrderCard) {
            toggleModal();
          } else {
            history.push('/dashboard/settings/account', { upgradeAccount: true });
          }
          break;
        case TIER_LEVEL.ONE:
        case TIER_LEVEL.TWO:
        case TIER_LEVEL.THREE:
        case TIER_LEVEL.FIVE:
        case TIER_LEVEL.SIX:
        case TIER_LEVEL.NINE:
        case TIER_LEVEL.TEN:
        default:
          if (allowOrderCard) {
            toggleModal();
          } else {
            showAlert({
              message: translate('feature_not_available_for_tier_level').replace(
                '__SUPPORT_EMAIL__',
                ledgerSupportEmailAddress
              ),
              type: 'danger',
            });
          }
          break;
      }
    } else if (isUserNotVerified) {
      history.push(`/kyc`, { switch: true });
    } else {
      toggleModal();
    }
  };

  return (
    <>
      <div className="d-none d-md-block">
        <CardLayout>
          <CardOrderComponent
            orderCardHandler={orderCardHandler}
            userVerificationState={verificationState}
            cardStatus={landingCardStatus ?? {}}
            defaultNetwork={defaultNetwork}
            userCardProvider={userCardProvider}
            isOrderCardAllowed={isOrderCardAllowed}
            isUsUser={isUsUser}
            isBannerVisible={isBannerVisible}
            userRegionSettings={userRegionSettings}
            cardType={cardType}
            orderableCardTypes={orderableCardTypes}
          />
        </CardLayout>
      </div>
      <OrderCardMobileComponent
        orderCardHandler={orderCardHandler}
        userVerificationState={verificationState}
        cardStatus={landingCardStatus ?? {}}
        defaultNetwork={defaultNetwork}
        userCardProvider={userCardProvider}
        isOrderCardAllowed={isOrderCardAllowed}
        isUsUser={isUsUser}
        showScreen={showScreen}
        bottomValue={bottomValue}
        userRegionSettings={userRegionSettings}
        cardType={cardType}
        orderableCardTypes={orderableCardTypes}
      />
      <ModalLayout
        onHide={() => setIsModalVisible(false)}
        isVisible={isModalVisible}
        modalHeight="h-95vh"
        currentScreen={currentScreen}>
        <InfoPopup keyName={orderCardFlowScreens.CARD_TERMS} options={cardTermsInfoOptions} />
      </ModalLayout>
    </>
  );
};

export default OrderCard;
